import React, { useEffect, useState } from 'react';
import { useApi } from '../../hooks/useApi';
import { useAuth } from '../../hooks/useAuth';
import { useClub } from '../../hooks/useClub';
import OutcomeModal from './OutcomeModal';
import LeadRecordPanel from '../lead/LeadRecordPanel';
import MismatchFlagModal from '../mismatch/MismatchFlagModal';

function interpolateScript(body, memberName, repName, clubName) {
  if (!body) return body;
  return body
    .replace(/\[Name\]/g, (() => { const n = memberName?.split(' ')[0]; return n ? n.charAt(0).toUpperCase() + n.slice(1) : '[Name]'; })())
    .replace(/\[Rep Name\]/g, repName || '[Rep Name]')
    .replace(/\[Club Name\]/g, clubName || '[Club Name]');
}

// ─── Inline Script Panel ─────────────────────────────────────────────────────
function InlineScriptPanel({ trialId, forDay, variant, memberName, repName, clubName }) {
  const { get } = useApi();
  const [script, setScript]         = useState(null);
  const [selectedKey, setSelectedKey] = useState(null);
  const [allScripts, setAllScripts] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [collapsed, setCollapsed]   = useState(false);
  const [copied, setCopied]         = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);

  useEffect(() => {
    if (!trialId) return;
    const url = forDay
      ? `/t2m/script/${trialId}?day=${forDay}`
      : `/t2m/script/${trialId}`;
    get(url)
      .then(s => { setScript(s); setSelectedKey(s?.scriptKey); })
      .catch(() => setScript(null))
      .finally(() => setLoading(false));
  }, [trialId, forDay]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!pickerOpen || allScripts.length > 0) return;
    get('/scripts?module=t2m')
      .then(d => setAllScripts(d?.scripts || []))
      .catch(() => setAllScripts([]));
  }, [pickerOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return null;
  if (!script || script.noScript) return null;

  const interpolatedBody = interpolateScript(script.body, memberName, repName, clubName);

  function handleCopy() {
    navigator.clipboard.writeText(interpolatedBody).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  function selectScript(s) {
    setScript({
      scriptName: s.name,
      scriptKey:  s.script_key,
      body: s.body,
      whyContext: s.why_context || s.whyContext,
      isClubOverride: s.scope === 'club',
    });
    setSelectedKey(s.script_key);
    setPickerOpen(false);
  }

  function channelLabel(s) {
    const order = s.contact_method_order || s.contactMethodOrder || [];
    if (!order.length) return null;
    const icons = { call: '📞', text: '💬', email: '📧' };
    return order.map(m => icons[m] || m).join(' → ');
  }

  return (
    <div style={{
      background: 'rgba(234,179,8,0.07)',
      border: '1px solid rgba(234,179,8,0.25)',
      borderRadius: 8, padding: '10px 14px',
      marginBottom: '1rem',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: collapsed ? 0 : 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--yellow)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            📋 {variant || script.scriptName}
          </span>
          {script.isClubOverride && (
            <span style={{ background: 'rgba(234,179,8,0.2)', color: 'var(--yellow)', fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 4 }}>CLUB OVERRIDE</span>
          )}
        </div>
        <button
          style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 11 }}
          onClick={() => setCollapsed(v => !v)}
        >
          {collapsed ? '▾ Show' : '▴ Hide'}
        </button>
      </div>

      {!collapsed && (
        <>
          {script.whyContext && (
            <div style={{ fontSize: 11, color: '#60a5fa', fontStyle: 'italic', marginBottom: 8, lineHeight: 1.4 }}>
              💡 {script.whyContext}
            </div>
          )}
          <div style={{
            fontSize: 13, color: 'var(--text)', lineHeight: 1.6,
            padding: '8px 12px', background: 'var(--surface-3)',
            borderRadius: 6, marginBottom: 8, whiteSpace: 'pre-wrap',
          }}>
            {interpolatedBody}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <button
              style={{ background: 'var(--yellow)', color: '#000', border: 'none', borderRadius: 5, padding: '4px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
              onClick={handleCopy}
            >
              {copied ? '✓ Copied' : '📋 Copy Script'}
            </button>
            <button
              style={{ background: 'none', border: '1px solid var(--border)', color: 'var(--text-muted)', borderRadius: 5, padding: '4px 10px', fontSize: 11, cursor: 'pointer' }}
              onClick={() => setPickerOpen(v => !v)}
            >
              {pickerOpen ? '▴ Close' : '↕ Use a different script'}
            </button>
          </div>

          {pickerOpen && (
            <div style={{ marginTop: 10, border: '1px solid var(--border)', borderRadius: 6, overflow: 'hidden' }}>
              <div style={{
                fontSize: 10, fontWeight: 700, color: '#888',
                textTransform: 'uppercase', letterSpacing: '0.06em',
                padding: '6px 10px', background: '#1a1a1a',
                borderBottom: '1px solid #222',
              }}>
                All T2M Scripts — tap to switch
              </div>
              {allScripts.length === 0 && (
                <div style={{ padding: '10px 12px', fontSize: 12, color: 'var(--text-muted)' }}>Loading scripts…</div>
              )}
              {allScripts.map((s, i) => (
                <button
                  key={s.script_id || i}
                  onClick={() => selectScript(s)}
                  style={{
                    width: '100%', textAlign: 'left',
                    padding: '8px 12px',
                    background: s.script_key === selectedKey ? 'rgba(234,179,8,0.1)' : '#111',
                    border: 'none',
                    borderBottom: i < allScripts.length - 1 ? '1px solid #222' : 'none',
                    cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
                  }}
                >
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#e5e5e5' }}>{s.name}</div>
                    {s.timing && <div style={{ fontSize: 10, color: '#888', marginTop: 1 }}>{s.timing}</div>}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                    {channelLabel(s) && (
                      <span style={{ fontSize: 10, color: '#888' }}>{channelLabel(s)}</span>
                    )}
                    {s.script_key === selectedKey && (
                      <span style={{ fontSize: 9, fontWeight: 700, color: '#facc15', background: 'rgba(234,179,8,0.15)', padding: '1px 6px', borderRadius: 3 }}>CURRENT</span>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─── Checkpoint script panel ──────────────────────────────────────────────────
function CheckpointScriptPanel({ trialId, trialDay, memberName, repName, clubName }) {
  if (!trialDay || trialDay < 3) return null;
  const label = trialDay >= 12 ? 'Day 12 Close Script'
    : trialDay >= 9  ? 'Day 9 Intervention Script'
    : trialDay >= 5  ? 'Day 5 Follow-Up Script'
    : 'Day 3 Check-In Script';
  return <InlineScriptPanel trialId={trialId} forDay={trialDay} variant={label} memberName={memberName} repName={repName} clubName={clubName} />;
}

// ─── Dropdown option lists ────────────────────────────────────────────────────

const TRIAL_SOURCES    = ['PB 101', 'Website', 'App', 'Walk-In', 'Referral', 'Lead Engine', 'Other'];
const LEVELS           = ['Beginner', 'Intermediate', 'Advanced', 'Unknown'];
const PLAY_TIMES       = ['Morning', 'Midday', 'Afternoon', 'Evening', 'Late Evening', 'Weekend Morning', 'Weekend Afternoon', 'Weekend Evening', 'Flexible'];
const ID_STATUSES      = ['Verified', 'Not Verified', 'Pending'];
const VERIFY_METHODS   = ['ID + Phone', 'Phone Only', 'ID Only', 'Mgr. Override'];
const SUGGESTION_DEPTS = ['Sales', 'F. Desk', 'Guest/App', 'Other'];
const INTERVENTION_TYPES = [
  'None Needed', 'Booked Session', 'Scheduled with Specific Group',
  'Manager Introduction', 'Skill/Program Recommendation', 'Explained Trial',
  'At Risk-Text/Call', 'Re-Engage: Past Trial Outreach', 'Resolve Issue', 'Other',
];
const TRIAL_STATUSES = ['New Trial', 'Active', 'On Track', 'At Risk', 'Ready to Close', 'Converted', 'Lost'];
const NEXT_ACTIONS   = [
  'Sales Follow-Up: Book Sessions', 'Sales Follow-Up: Close Membership',
  'Club: FD: Book Program', 'Club: FD: Reinforce Next Visit',
  'Manager: Intro Needed', 'Manager: Connection Visit', 'None',
];
const FINAL_OUTCOMES = [
  'Unlimited Nationwide - Full Price', 'Unlimited Nationwide - 10% Discount (Closer)',
  'Unlimited Club Only - 20% Discount (Closer)', 'Social Membership',
  'Junior Membership', 'Punch Card', 'April Promo', 'No Conversion', 'Re-Engage', 'Lost',
];
const CURRENT_TASKS = [
  'Day 1 Welcome Text',
  'Day 3 Check-In Call',
  'Day 5 Follow-Up Call',
  'Day 9 Intervention Call',
  'Day 12 Close',
  'Manager Connection',
  'Verify ID',
  'Book Session',
  'Follow Up',
  'None',
];

// ─── Auto-suggest current task by trial day ───────────────────────────────────
function suggestTask(trialDay) {
  if (!trialDay) return 'Day 1 Welcome Text';
  if (trialDay >= 12) return 'Day 12 Close';
  if (trialDay >= 9)  return 'Day 9 Intervention Call';
  if (trialDay >= 5)  return 'Day 5 Follow-Up Call';
  if (trialDay >= 3)  return 'Day 3 Check-In Call';
  return 'Day 1 Welcome Text';
}

// ─── Stepper ──────────────────────────────────────────────────────────────────

const STEPS = [
  { num: 1, label: 'Onboarding',   sub: 'Sign-up · Verify · Level' },
  { num: 2, label: 'Programming',  sub: 'Sessions · Booking' },
  { num: 3, label: 'Checkpoints',  sub: 'Visits · Intervention' },
  { num: 4, label: 'Close',        sub: 'Offer · Outcome' },
];

function Stepper({ current, onChange }) {
  return (
    <div style={styles.stepper}>
      {STEPS.map((s, i) => {
        const done   = s.num < current;
        const active = s.num === current;
        return (
          <React.Fragment key={s.num}>
            <button
              style={{
                ...styles.stepBtn,
                ...(active ? styles.stepActive : {}),
                ...(done   ? styles.stepDone   : {}),
              }}
              onClick={() => onChange(s.num)}
            >
              {done && <span style={styles.stepNum}>✓</span>}
              <span style={styles.stepLabel}>{s.label}</span>
              <span style={styles.stepSub}>{s.sub}</span>
            </button>
            {i < STEPS.length - 1 && (
              <div style={{
                ...styles.stepConnector,
                ...(done ? styles.stepConnectorDone : {}),
              }} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ─── Field helpers ────────────────────────────────────────────────────────────

function Field({ label, children, span }) {
  return (
    <div className="field-group" style={span ? { gridColumn: 'span 2' } : {}}>
      <label className="field-label">{label}</label>
      {children}
    </div>
  );
}

function ReadRow({ label, value, highlight }) {
  return (
    <div style={styles.readRow}>
      <span style={styles.readLabel}>{label}</span>
      <span style={{ ...styles.readValue, ...(highlight ? { color: 'var(--yellow)', fontWeight: 600 } : {}) }}>
        {value !== undefined && value !== null && value !== '' ? String(value) : '—'}
      </span>
    </div>
  );
}

function SelectField({ label, value, options, onChange, span }) {
  return (
    <Field label={label} span={span}>
      <select className="field-select" value={value || ''} onChange={e => onChange(e.target.value)}>
        <option value="">Select...</option>
        {options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    </Field>
  );
}

function InputField({ label, value, onChange, span, placeholder }) {
  return (
    <Field label={label} span={span}>
      <input
        className="field-input"
        value={value || ''}
        placeholder={placeholder}
        onChange={e => onChange(e.target.value)}
      />
    </Field>
  );
}

function CheckField({ label, value, onChange }) {
  return (
    <div className="field-group">
      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
        <input type="checkbox" checked={!!value} onChange={e => onChange(e.target.checked)} />
        <span className="field-label" style={{ margin: 0 }}>{label}</span>
      </label>
    </div>
  );
}

function VisitDayBadge({ day, visits }) {
  const color = visits > 0 ? 'badge-green' : 'badge-gray';
  return (
    <div style={styles.visitBadge}>
      <div style={styles.visitBadgeDay}>Day {day}</div>
      <span className={`badge ${color}`}>{visits} visit{visits !== 1 ? 's' : ''}</span>
    </div>
  );
}

function SectionDivider({ label }) {
  return (
    <div style={styles.sectionDivider}>
      <span style={styles.sectionDividerLabel}>{label}</span>
    </div>
  );
}

// ─── Trial Status Badge ───────────────────────────────────────────────────────

function TrialStatusBadge({ status, riskLevel }) {
  if (riskLevel === 'at_risk' || status?.toLowerCase() === 'at risk') {
    return <span className="badge badge-red">At Risk</span>;
  }
  if (status === 'converted') return <span className="badge badge-green">Converted</span>;
  if (status === 'lost')      return <span className="badge badge-gray">Lost</span>;
  if (status === 'Ready to Close') return <span className="badge badge-orange">Ready to Close</span>;
  if (status === 'On Track')  return <span className="badge badge-blue">On Track</span>;
  return <span className="badge badge-blue">{status || 'Active'}</span>;
}

// ─── PbP Profile Field ────────────────────────────────────────────────────────

function PbpField({ label, value, badge }) {
  const badgeColor = badge === 'PBP FIXED' ? '#6b46c1' : '#1a4a6b';
  return (
    <div>
      <div style={{
        fontSize: 11, color: 'var(--text-secondary)',
        marginBottom: 3, textTransform: 'uppercase', letterSpacing: '0.06em',
      }}>
        {label}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 14, color: 'var(--text)' }}>{value}</span>
        <span style={{
          fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 3,
          background: badgeColor, color: '#fff', letterSpacing: '0.06em',
        }}>
          {badge}
        </span>
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function T2MTrialDetail({ trialId, onBack }) {
  const { user }        = useAuth();
  const { currentClub } = useClub();
  const repName  = user?.name || user?.email || '[Rep Name]';
  const clubName = currentClub?.name || '[Club Name]';
  const { get, put }  = useApi();
  const [trial, setTrial]             = useState(null);
  const [loading, setLoading]         = useState(true);
  const [saving, setSaving]           = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [showOutcome, setShowOutcome] = useState(false);
  const [showTimeline, setShowTimeline] = useState(false);
  const [showMismatch, setShowMismatch] = useState(false);
  const [form, setForm]               = useState({});
  const [dirty, setDirty]             = useState(false);
  const [editingTask, setEditingTask] = useState(false);

  function load() {
    setLoading(true);
    get(`/t2m/trial/${trialId}`)
      .then(d => {
        setTrial(d);
        setForm({
          ...d,
          currentTask:          d.currentTask || suggestTask(d.trialDay),
          membershipSold:       d.membershipSold       ?? false,
          membershipOfferMade:  d.membershipOfferMade  ?? false,
        });
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }

  useEffect(() => { load(); }, [trialId]);

  function update(field, value) {
    setForm(f => ({ ...f, [field]: value }));
    setDirty(true);
  }

  async function handleSave() {
    setSaving(true);
    try {
      await put(`/t2m/trial/${trialId}`, {
        phone:                form.phone,
        email:                form.email,
        skill_level:          form.skillLevel,
        trial_source:         form.trialSource,
        trial_start_date:     form.startDate || undefined,
        preferred_time_1:     form.preferredTime1,
        preferred_time_2:     form.preferredTime2,
        suggested_by:         form.suggestedBy,
        suggestion_dept:      form.suggestionDept,
        suggestions_given:    form.suggestionsGiven,
        confirm_booked:       form.confirmBooked,
        booked_by:            form.bookedBy,
        programs_booked:      form.programsBooked,
        id_verify_status:     form.verificationStatus,
        verify_method:        form.verifyMethod,
        intervention_made:    form.interventionMade,
        intervention_type:    form.interventionType,
        intervention_by:      form.interventionBy,
        intervention_date:    form.interventionDate,
        manager_connection:   form.managerConnection,
        manager_name:         form.managerName,
        trial_status:         form.status,
        next_action:          form.nextAction,
        conversion_offer_date: form.conversionOfferDate,
        promo_presented:       form.promoPresented,
        final_outcome:         form.finalOutcome,
        notes:                 form.notes,
        current_task:          form.currentTask,
        membership_sold:       form.membershipSold       ?? false,
        membership_offer_made: form.membershipOfferMade  ?? false,
      });
      setDirty(false);
      setEditingTask(false);
      load();
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  }

  // ── Derived PbP fields ──
  const hasPbpData = trial && (
    trial.pbpSkillLevel || trial.pbpDob || trial.pbpGender ||
    trial.pbpMembershipType || trial.pbpStatus || trial.pbpFirstSeen
  );

  // ── Next Action due label ──
  function nextActionDueLabel() {
    if (!trial?.trialDay) return null;
    if (trial.trialDay >= 12) return 'Today — Day 12';
    if (trial.trialDay >= 9)  return 'Approaching — Day 9+';
    return null;
  }

  if (loading) return <div className="empty-state"><div className="spinner" /></div>;
  if (!trial)  return <div className="empty-state"><p>Record not found.</p></div>;

  return (
    <div style={{ maxWidth: 720 }}>

      {/* Back + name + status badges + Log Contact + Next Action */}
      <div style={{ marginBottom: '1rem', display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
        <button className="btn btn-ghost btn-sm" onClick={onBack}>← Back</button>
        <span style={{ fontSize: 18, fontWeight: 700, color: 'var(--text)' }}>{trial.full_name || trial.name}</span>
        {trial.riskLevel === 'at_risk' && <span className="badge badge-red">At Risk</span>}
        {trial.riskLevel === 'warning'  && <span className="badge badge-orange">Watch</span>}
        {trial.status === 'converted'   && <span className="badge badge-green">Converted</span>}
        {trial.status === 'lost'        && <span className="badge badge-gray">Lost</span>}
        {trial.createdSource === 'manual'   && <span className="badge badge-gray">Manual Entry</span>}
        {trial.createdSource === 'migrated' && <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 3, background: '#1a4a6b', color: '#fff', letterSpacing: '0.06em' }}>MIGRATED</span>}

        {trial.nextAction && trial.nextAction !== 'None' && (
          <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
            <div style={{ fontSize: 10, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 2 }}>
              Next Action
            </div>
            <div style={{ color: 'var(--yellow)', fontWeight: 600, fontSize: 13, lineHeight: 1.2 }}>
              {trial.nextAction}
            </div>
            {nextActionDueLabel() && (
              <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 1 }}>
                {nextActionDueLabel()}
              </div>
            )}
          </div>
        )}

        {!trial.nextAction || trial.nextAction === 'None' ? (
          <span style={{ marginLeft: 'auto', display: 'flex', gap: '0.5rem' }}>
            <button className="btn btn-ghost btn-sm" onClick={() => setShowTimeline(true)}>📋 Timeline</button>
            <button className="btn btn-ghost btn-sm" onClick={() => setShowMismatch(true)} style={{ color: "#ef4444", borderColor: "rgba(239,68,68,0.3)" }}>🚩 Flag</button>
            <button className="btn btn-primary btn-sm" onClick={() => setShowOutcome(true)}>Log Contact</button>
          </span>
        ) : (
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button className="btn btn-ghost btn-sm" onClick={() => setShowTimeline(true)}>📋 Timeline</button>
            <button className="btn btn-ghost btn-sm" onClick={() => setShowMismatch(true)} style={{ color: "#ef4444", borderColor: "rgba(239,68,68,0.3)" }}>🚩 Flag</button>
            <button className="btn btn-primary btn-sm" onClick={() => setShowOutcome(true)}>Log Contact</button>
          </div>
        )}
      </div>

      {/* Activation source banner */}
      {trial.activationName && (
        <div style={{
          display: 'flex', alignItems: 'flex-start', gap: 10,
          padding: '10px 14px', marginBottom: '0.75rem',
          background: 'rgba(168,85,247,0.08)',
          border: '1px solid rgba(168,85,247,0.25)',
          borderRadius: 8,
        }}>
          <span style={{ fontSize: 16 }}>⚡</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#a855f7', marginBottom: 2 }}>
              From Activation: {trial.activationName}
            </div>
            {trial.inboundNotes && (
              <div style={{ fontSize: 11, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                {trial.inboundNotes.split(' | ').map((note, i) => (
                  <span key={i} style={{ display: 'inline-block', marginRight: 8 }}>
                    · {note.replace(/^interest:\s*/i, '').replace(/^custom_[^:]+:\s*/i, '')}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Quick stats bar */}
      <div style={styles.statsBar}>
        <span>📅 Day {trial.trialDay}</span>
        <span>🏃 {trial.visits} visit{trial.visits !== 1 ? 's' : ''}</span>
        <span>🎯 {trial.skillLevel || 'Level unknown'}</span>
        <span>🏠 Club {trial.clubId}</span>
        {trial.lastVisitDate && <span>Last visit {trial.lastVisitDate}</span>}
        {trial.suggestedBy && (
          <span style={{ color: 'var(--yellow)', fontWeight: 600 }}>
            💡 Suggested by {trial.suggestedBy}{trial.suggestionDept ? ` · ${trial.suggestionDept}` : ''}
          </span>
        )}

        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 10, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 2 }}>
              Current Task
            </div>
            {editingTask ? (
              <select
                className="field-select"
                style={{ fontSize: 12, padding: '2px 6px', height: 'auto' }}
                value={form.currentTask || ''}
                onChange={e => { update('currentTask', e.target.value); }}
                autoFocus
                onBlur={() => setEditingTask(false)}
              >
                <option value="">Select...</option>
                {CURRENT_TASKS.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            ) : (
              <div
                style={{ color: 'var(--yellow)', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}
                onClick={() => setEditingTask(true)}
                title="Click to edit"
              >
                {form.currentTask || suggestTask(trial.trialDay)}
                <span style={{ fontSize: 10, color: 'var(--text-secondary)', marginLeft: 4 }}>✎</span>
              </div>
            )}
          </div>
          <TrialStatusBadge status={form.status || trial.status} riskLevel={trial.riskLevel} />
        </div>
      </div>

      {/* Stepper */}
      <Stepper current={currentStep} onChange={setCurrentStep} />

      {/* Step content card */}
      <div style={styles.stepContent}>

        {/* ── TAB 1: Onboarding ── */}
        {currentStep === 1 && (
          <>
            <div style={styles.stepTitle}>
              Onboarding <span style={styles.stepOwner}>F. Desk · Sales</span>
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
                Day 1 Welcome Script
              </div>
              <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                <span style={{ background: 'rgba(234,179,8,0.15)', color: 'var(--yellow)', border: '1px solid rgba(234,179,8,0.3)', borderRadius: 4, padding: '3px 10px', fontSize: 11, fontWeight: 700 }}>
                  📞 In-Person — send after they leave
                </span>
                <span style={{ background: 'rgba(96,165,250,0.1)', color: '#60a5fa', border: '1px solid rgba(96,165,250,0.3)', borderRadius: 4, padding: '3px 10px', fontSize: 11, fontWeight: 700 }}>
                  💬 Online Purchase — send on Day 1
                </span>
              </div>
              <InlineScriptPanel trialId={trialId} forDay={1} variant="Day 1 Welcome Text" memberName={trial?.full_name || trial?.name} repName={repName} clubName={clubName} />
            </div>

            <SectionDivider label="Member Info" />
            <div style={styles.grid2}>
              <InputField label="Phone" value={form.phone} onChange={v => update('phone', v)} placeholder="10-digit" />
              <InputField label="Email" value={form.email} onChange={v => update('email', v)} span />
              <SelectField label="Trial Source" value={form.trialSource} options={TRIAL_SOURCES} onChange={v => update('trialSource', v)} />
              <SelectField label="Skill Level"  value={form.skillLevel}  options={LEVELS}        onChange={v => update('skillLevel', v)} />
              <SelectField label="Preferred Time 1" value={form.preferredTime1} options={PLAY_TIMES} onChange={v => update('preferredTime1', v)} />
              <SelectField label="Preferred Time 2" value={form.preferredTime2} options={PLAY_TIMES} onChange={v => update('preferredTime2', v)} />
              <InputField label="Trial Start Date" value={form.startDate ? new Date(form.startDate).toISOString().split('T')[0] : ''} onChange={v => update('startDate', v)} placeholder="YYYY-MM-DD" />
            </div>
            <div style={styles.grid2}>
              <ReadRow label="Trial Day" value={`Day ${trial.trialDay}`} />
            </div>

            <SectionDivider label="ID Verification — complete by 2nd visit" />
            <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 10, lineHeight: 1.5 }}>
              Ask to see ID · Confirm email matches PbP · Send a quick text from AirCall to confirm cell number.
            </div>
            <div style={styles.grid2}>
              <SelectField label="ID Status"     value={form.verificationStatus} options={ID_STATUSES}    onChange={v => update('verificationStatus', v)} />
              <SelectField label="Verify Method" value={form.verifyMethod}       options={VERIFY_METHODS} onChange={v => update('verifyMethod', v)} />
            </div>

            <Field label="Notes" span>
              <textarea className="field-textarea" value={form.notes || ''} onChange={e => update('notes', e.target.value)} placeholder="Add notes (saved with record)..." />
            </Field>

            {hasPbpData && (
              <div style={{
                marginTop: 20, padding: '14px 18px',
                background: 'var(--surface-3, #111)',
                border: '1px solid var(--border)', borderRadius: 8,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                    PbP Profile Data
                  </span>
                  <span style={{ fontSize: 10, color: 'var(--text-secondary)', fontStyle: 'italic' }}>
                    Read only · from PbP
                  </span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px 32px' }}>
                  {trial.pbpSkillLevel    && <PbpField label="Level"           value={trial.pbpSkillLevel} badge="PBP AUTO" />}
                  {trial.pbpDob          && <PbpField label="Date of Birth"    value={new Date(trial.pbpDob).toLocaleDateString()} badge="PBP AUTO" />}
                  {trial.pbpGender       && <PbpField label="Gender"           value={trial.pbpGender} badge="PBP FIXED" />}
                  {trial.pbpMembershipType && <PbpField label="Membership Type" value={trial.pbpMembershipType} badge="PBP AUTO" />}
                  {trial.pbpStatus       && <PbpField label="PbP Status"       value={trial.pbpStatus} badge="PBP AUTO" />}
                  {trial.pbpFirstSeen    && <PbpField label="First Seen Date"  value={new Date(trial.pbpFirstSeen).toLocaleDateString()} badge="PBP AUTO" />}
                  {trial.duprRating      && <PbpField label="DUPR Rating"      value={trial.duprRating} badge="PBP AUTO" />}
                </div>
              </div>
            )}
          </>
        )}

        {/* ── TAB 2: Programming ── */}
        {currentStep === 2 && (
          <>
            <div style={styles.stepTitle}>
              Programming <span style={styles.stepOwner}>Sales or F. Desk</span>
            </div>

            {(form.skillLevel || form.preferredTime1 || form.preferredTime2) ? (
              <div style={{
                background: 'var(--surface-3)', border: '1px solid var(--border)',
                borderRadius: 8, padding: '10px 14px', marginBottom: '1rem',
                display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px 16px',
              }}>
                <div>
                  <div style={{ fontSize: 10, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>Level</div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{form.skillLevel || '—'}</div>
                </div>
                <div>
                  <div style={{ fontSize: 10, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>Preferred Time 1</div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{form.preferredTime1 || '—'}</div>
                </div>
                <div>
                  <div style={{ fontSize: 10, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>Preferred Time 2</div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{form.preferredTime2 || '—'}</div>
                </div>
              </div>
            ) : (
              <div style={{
                background: 'rgba(234,179,8,0.08)', border: '1px solid rgba(234,179,8,0.3)',
                borderRadius: 8, padding: '10px 14px', marginBottom: '1rem',
                fontSize: 12, color: 'var(--yellow)',
              }}>
                ⚠ Complete <strong>Skill Level</strong> and <strong>Preferred Times</strong> in the Onboarding tab before booking sessions.
              </div>
            )}

            <InlineScriptPanel trialId={trialId} forDay={1} variant="Day 1 Onboarding Conversation" memberName={trial?.full_name || trial?.name} repName={repName} clubName={clubName} />

            <div style={styles.grid2}>
              <InputField  label="Suggested By"      value={form.suggestedBy}      onChange={v => update('suggestedBy', v)}      placeholder="Staff name" />
              <SelectField label="Suggestion Dept"   value={form.suggestionDept}   options={SUGGESTION_DEPTS} onChange={v => update('suggestionDept', v)} />
              <InputField  label="Suggestions Given" value={form.suggestionsGiven} onChange={v => update('suggestionsGiven', v)} span placeholder="Programs or sessions suggested" />
              <CheckField  label="Confirmed Booked?" value={form.confirmBooked}    onChange={v => update('confirmBooked', v)} />
              <InputField  label="Booked By"         value={form.bookedBy}         onChange={v => update('bookedBy', v)}         placeholder="Staff name" />
              <InputField  label="Programs Booked"   value={form.programsBooked}   onChange={v => update('programsBooked', v)}   span placeholder="e.g. Open Play Monday 6pm" />
            </div>

            <Field label="Notes" span>
              <textarea className="field-textarea" value={form.notes || ''} onChange={e => update('notes', e.target.value)} />
            </Field>
          </>
        )}

        {/* ── TAB 3: Checkpoints ── */}
        {currentStep === 3 && (
          <>
            <div style={styles.stepTitle}>
              Checkpoints <span style={styles.stepOwner}>Updated daily · Opener</span>
            </div>

            <CheckpointScriptPanel trialId={trialId} trialDay={trial.trialDay} />

            <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
              <VisitDayBadge day={3}  visits={trial.visitsDay3}  />
              <VisitDayBadge day={5}  visits={trial.visitsDay5}  />
              <VisitDayBadge day={9}  visits={trial.visitsDay9}  />
              <VisitDayBadge day={12} visits={trial.visitsDay12} />
            </div>

            <div style={styles.grid2}>
              <ReadRow label="Total Visits"    value={trial.visits} />
              <ReadRow label="Last Visit Date" value={trial.lastVisitDate ? new Date(trial.lastVisitDate).toLocaleDateString() : '—'} />
              <ReadRow label="At Risk?"        value={trial.riskLevel === 'at_risk' ? 'Yes' : 'No'} highlight={trial.riskLevel === 'at_risk'} />
            </div>

            <SectionDivider label="Intervention" />
            <div style={styles.grid2}>
              <CheckField  label="Intervention Made?"  value={form.interventionMade} onChange={v => update('interventionMade', v)} />
              <SelectField label="Intervention Taken"  value={form.interventionType} options={INTERVENTION_TYPES} onChange={v => update('interventionType', v)} />
              <InputField  label="Intervention By"     value={form.interventionBy}   onChange={v => update('interventionBy', v)}   placeholder="Staff name" />
              <InputField  label="Intervention Date"   value={form.interventionDate} onChange={v => update('interventionDate', v)} placeholder="MM/DD/YYYY" />
            </div>

            <SectionDivider label="Manager Connection" />
            <div style={styles.grid2}>
              <CheckField label="Manager Connection Made?" value={form.managerConnection} onChange={v => update('managerConnection', v)} />
              <InputField label="Manager Name"             value={form.managerName}       onChange={v => update('managerName', v)} placeholder="Manager name" />
              <ReadRow    label="Connection Date"          value={trial.connectionDate ? new Date(trial.connectionDate).toLocaleDateString() : '—'} />
            </div>

            <Field label="Notes" span>
              <textarea className="field-textarea" value={form.notes || ''} onChange={e => update('notes', e.target.value)} placeholder="Add notes (saved with record)..." />
            </Field>
          </>
        )}

        {/* ── TAB 4: Close ── */}
        {currentStep === 4 && (
          <>
            <div style={styles.stepTitle}>
              Close <span style={styles.stepOwner}>Sales · Day 10–14</span>
            </div>

            <InlineScriptPanel trialId={trialId} forDay={12} variant="Day 12 Close Script" memberName={trial?.full_name || trial?.name} repName={repName} clubName={clubName} />

            <div style={styles.recapBox}>
              <div style={styles.recapTitle}>Trial Summary</div>
              <ReadRow label="Total Visits"      value={trial.visits} />
              <ReadRow label="Last Visit"        value={trial.lastVisitDate ? new Date(trial.lastVisitDate).toLocaleDateString() : '—'} />
              <ReadRow label="Verification"      value={trial.verificationStatus ? trial.verificationStatus.charAt(0).toUpperCase() + trial.verificationStatus.slice(1) : '—'} />
              <ReadRow label="Manager Connected" value={trial.managerConnection ? 'Yes' : 'No'} />
              <ReadRow label="Intervention Made" value={trial.interventionMade  ? 'Yes' : 'No'} />
              <ReadRow label="Level"             value={trial.skillLevel || '—'} />
            </div>

            <div style={styles.grid2}>
              <SelectField label="Trial Status"           value={form.status}              options={TRIAL_STATUSES} onChange={v => update('status', v)} />
              <SelectField label="Next Action"            value={form.nextAction}          options={NEXT_ACTIONS}   onChange={v => update('nextAction', v)} />
              <InputField  label="Offer Date"             value={form.conversionOfferDate} onChange={v => update('conversionOfferDate', v)} placeholder="MM/DD/YYYY" />
              <CheckField  label="Promo Presented?"       value={form.promoPresented}      onChange={v => update('promoPresented', v)} />
              <CheckField  label="Membership Offer Made?" value={form.membershipOfferMade} onChange={v => update('membershipOfferMade', v)} />
              <CheckField  label="Membership Sold?"       value={form.membershipSold}      onChange={v => update('membershipSold', v)} />
              <SelectField label="Final Outcome"          value={form.finalOutcome}        options={FINAL_OUTCOMES} onChange={v => update('finalOutcome', v)} span />
            </div>

            <Field label="Notes" span>
              <textarea className="field-textarea" value={form.notes || ''} onChange={e => update('notes', e.target.value)} />
            </Field>
          </>
        )}

        {/* Step nav + save */}
        <div style={styles.stepNav}>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            {currentStep > 1 && (
              <button className="btn btn-ghost btn-sm" onClick={() => setCurrentStep(s => s - 1)}>← Back</button>
            )}
            {currentStep < 4 && (
              <button className="btn btn-ghost btn-sm" onClick={() => setCurrentStep(s => s + 1)}>Next →</button>
            )}
          </div>
          <button
            className="btn btn-primary btn-sm"
            onClick={handleSave}
            disabled={!dirty || saving}
          >
            {saving ? 'Saving…' : dirty ? 'Save Changes' : 'Saved'}
          </button>
        </div>
      </div>

      {showTimeline && (
        <LeadRecordPanel
          actionId={trial.actionId || null}
          memberId={trial.memberId || trial.member_id}
          memberName={trial.full_name || trial.name}
          onClose={() => setShowTimeline(false)}
        />
      )}

      {showMismatch && (
        <MismatchFlagModal
          module="t2m"
          recordId={trial.trial_id || trialId}
          memberId={trial.memberId || trial.member_id}
          locationId={trial.clubId || trial.location_id}
          memberName={trial.full_name || trial.name}
          originalStatus={trial.status || trial.trial_status}
          onClose={() => setShowMismatch(false)}
          onFlagged={() => { setShowMismatch(false); load(); }}
        />
      )}
      {showOutcome && (
        <OutcomeModal
          trial={trial}
          onClose={() => setShowOutcome(false)}
          onSaved={() => { setShowOutcome(false); load(); }}
        />
      )}
    </div>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const styles = {
  stepper: {
    display: 'flex', alignItems: 'stretch', marginBottom: '1.25rem',
    overflowX: 'auto', paddingBottom: '0.25rem', gap: 0,
  },
  stepBtn: {
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    justifyContent: 'center', padding: '0.6rem 0.5rem',
    border: '1px solid var(--border)', borderRadius: '8px',
    background: 'var(--surface-2)', color: 'var(--text)',
    cursor: 'pointer', width: 130, minWidth: 130, flexShrink: 0,
    gap: '0.15rem', transition: 'all 0.15s', textAlign: 'center',
  },
  stepActive:       { borderColor: 'var(--yellow)', background: 'var(--yellow)', color: '#000' },
  stepDone:         { borderColor: 'var(--green)', color: 'var(--green)', background: 'transparent' },
  stepNum:          { fontSize: '1rem', fontWeight: 700, lineHeight: 1 },
  stepLabel:        { fontSize: '0.75rem', fontWeight: 600, lineHeight: 1.2, whiteSpace: 'nowrap' },
  stepSub:          { fontSize: '0.62rem', opacity: 0.75, lineHeight: 1.2, whiteSpace: 'nowrap' },
  stepConnector:    { alignSelf: 'center', width: 16, minWidth: 16, flexShrink: 0, height: 2, background: 'var(--border)' },
  stepConnectorDone:{ background: 'var(--green)' },
  statsBar: {
    display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'center',
    padding: '0.6rem 0.75rem', background: 'var(--surface-2)',
    border: '1px solid var(--border)', borderRadius: '8px',
    fontSize: '0.82rem', color: 'var(--text-secondary)', marginBottom: '1rem',
  },
  stepContent: {
    background: 'var(--surface-2)', border: '1px solid var(--border)',
    borderRadius: '10px', padding: '1.25rem', marginBottom: '1rem',
  },
  stepTitle:        { fontSize: '1rem', fontWeight: 700, color: 'var(--text)', marginBottom: '1rem' },
  stepOwner:        { fontSize: '0.75rem', fontWeight: 400, color: 'var(--text-secondary)', marginLeft: '0.5rem' },
  grid2:            { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' },
  readRow:          { display: 'flex', justifyContent: 'space-between', padding: '0.35rem 0', borderBottom: '1px solid var(--border)', fontSize: '0.83rem' },
  readLabel:        { color: 'var(--text-secondary)', fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.04em' },
  readValue:        { color: 'var(--text)' },
  sectionDivider:   { borderTop: '1px solid var(--border)', margin: '1rem 0 0.75rem', paddingTop: '0.75rem' },
  sectionDividerLabel: { fontSize: '0.72rem', fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.06em' },
  visitBadgeRow:    { display: 'flex', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap' },
  visitBadge:       { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.25rem', padding: '0.5rem 0.75rem', background: 'var(--surface-3)', border: '1px solid var(--border)', borderRadius: '8px', minWidth: 70 },
  visitBadgeDay:    { fontSize: '0.7rem', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em' },
  recapBox:         { background: 'var(--surface-3)', border: '1px solid var(--border)', borderRadius: '8px', padding: '0.75rem 1rem', marginBottom: '1rem' },
  recapTitle:       { fontSize: '0.72rem', fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.5rem' },
  stepNav:          { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '1.25rem', paddingTop: '1rem', borderTop: '1px solid var(--border)' },
};
